// Define the DynamoDBItem type
export interface DynamoDBItem {
    [key: string]: any;
}

// Pas 1.1: local AttributeValue <-> JS conversion, replacing the
// aws-sdk v2 browser bundle (window.AWS.DynamoDB.Converter) that used
// to load render-blocking from <head> on every page just for this.
// Semantics mirror Converter.marshall/unmarshall for the shapes the
// API actually sends: S, N, B, BOOL, NULL, M, L, SS, NS, BS.

// DynamoDB AttributeValue type codes (aws-sdk v2 Converter shape).
const DDB_TYPE_KEYS = new Set(['S', 'N', 'B', 'BOOL', 'NULL', 'M', 'L', 'SS', 'NS', 'BS']);

function isAttrValue(v: any): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  return keys.length === 1 && DDB_TYPE_KEYS.has(keys[0]);
}

// True when every top-level value is an AttributeValue ({S:...}, {N:...},
// {M:...}, ...). Plain-JSON items (already unmarshalled by the backend)
// must be passed through untouched: unmarshalling a plain object as if
// it were an AttributeMap silently returns {} instead of throwing,
// which used to wipe every workspace/project list down to blank entries.
export function isDynamoDBTypedItem(item: any): boolean {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
  const keys = Object.keys(item);
  if (keys.length === 0) return false;
  return keys.every((k) => isAttrValue(item[k]));
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

export function unmarshallValue(attr: any): any {
  if (!isAttrValue(attr)) return attr;
  const key = Object.keys(attr)[0];
  const val = attr[key];
  switch (key) {
    case 'S': return val;
    case 'N': return Number(val);
    case 'B': return typeof val === 'string' ? base64ToBytes(val) : val;
    case 'BOOL': return Boolean(val);
    case 'NULL': return null;
    case 'M': return unmarshallItem(val);
    case 'L': return (val as any[]).map((v) => unmarshallValue(v));
    case 'SS': return [...(val as string[])];
    case 'NS': return (val as string[]).map((v) => Number(v));
    case 'BS': return (val as any[]).map((v) => (typeof v === 'string' ? base64ToBytes(v) : v));
    default: return val;
  }
}

export function unmarshallItem(item: any): DynamoDBItem {
  const out: DynamoDBItem = {};
  if (!item || typeof item !== 'object') return out;
  for (const k of Object.keys(item)) out[k] = unmarshallValue(item[k]);
  return out;
}

export function marshallValue(value: any): any {
  if (value === null || value === undefined) return { NULL: true };
  if (typeof value === 'string') return { S: value };
  if (typeof value === 'number') return { N: String(value) };
  if (typeof value === 'boolean') return { BOOL: value };
  if (value instanceof Uint8Array) return { B: bytesToBase64(value) };
  if (Array.isArray(value)) return { L: value.map((v) => marshallValue(v)) };
  if (value instanceof Set) {
    const arr = [...value];
    if (arr.every((v) => typeof v === 'string')) return { SS: arr };
    if (arr.every((v) => typeof v === 'number')) return { NS: arr.map((v) => String(v)) };
    return { L: arr.map((v) => marshallValue(v)) };
  }
  if (typeof value === 'object') return { M: marshallItem(value) };
  return { S: String(value) };
}

export function marshallItem(item: any): DynamoDBItem {
  const out: DynamoDBItem = {};
  if (!item || typeof item !== 'object') return out;
  for (const k of Object.keys(item)) {
    // Converter.marshall drops undefined map entries.
    if (item[k] !== undefined) out[k] = marshallValue(item[k]);
  }
  return out;
}

function unmarshallIfTyped(item: any): any {
  return isDynamoDBTypedItem(item) ? unmarshallItem(item) : item;
}

// Function to unmarshal an array of DynamoDBDocument JSON
export function unmarshalDynamoDBDocumentGood(json:  any): DynamoDBItem[] {
  try
  {
    if (json.Items != undefined) json.Items = json.Items.map((i: any) => unmarshallIfTyped(i));
    if (json.Item != undefined) json.Item = unmarshallIfTyped(json.Item);
    if (json.Attributes != undefined) json.Attributes = unmarshallIfTyped(json.Attributes);
    if (json.LastEvaluatedKey != undefined) json.LastEvaluatedKey = unmarshallIfTyped(json.LastEvaluatedKey);
    return json;
  }
  catch (e)
  {
    return json;
  }

}


// Function to unmarshal an array of DynamoDBDocument JSON
export function unmarshalDynamoDBDocument(json:  any): DynamoDBItem[] {
  if (json.Items != undefined)
  {
    return json.Items.map((i: any) => unmarshallItem(i));
  }
  else if (json.Item != undefined){
    return [unmarshallItem(json.Item)];
  }
  else
  {
    return unmarshallItem(json) as DynamoDBItem[];
  }
}

export function marshalDynamoDBDocument(json: any): any {
  return marshallItem(json);
}
